/** The Trips panel's model (plan "Page anatomy" → Trips panel; D4, D5) — pure, `now` a
 *  parameter. The panel is a pinned view of `GET /bookings/`: one query per group, each
 *  pinned to the user by id and to one `phase` (the spine's rule, `tripPhase`), with the
 *  row expansions the trip rows read.
 *
 *  - **Groups** — Now (active) · Upcoming (future, ascending) · Past (descending) ·
 *    Undated (only if any); exact counts (`count_mode=sync`).
 *  - **Schedule axis** — one linear window around now (`AXIS_RANGES`); every trip a bar
 *    from start to end in packed lanes (at most 5, then "+N overlapping"); a zero-length
 *    trip the booked tick; for a staff booking the guide-app window (solid; dashed when
 *    the guide's cache does not hold it) and the chat window (dotted) under its bar.
 *    Ticks are dates in the **dominant zone** of the loaded trips.
 *  - **When** — the row's first column, in the trip's own zone: "12–14 Oct · 2 d 9 h" (the
 *    real span, `formatTripDuration` — a 6-hour overnight trip is "6 h", not "2 days"),
 *    "Fri 21 Nov · 07:45–13:45", active "… · day 3 of 7" (a trip of a day or more). An
 *    all-day trip reads as the booking record does: the start day "· all day", and its end
 *    — the next local midnight — in full ("ends Sat 3 Oct 00:00", the spine's After node). */
import type { BookingListExpansion, BookingListItem, BookingListParams } from '@/api/bookings';
import { expansionsForColumns } from '@/features/bookings/bookingsColumns';
import { guidesText, integrationName } from '@/features/bookings/tripExpansionCells';
import { formatTripDuration } from '@/features/records/time/tripDuration';
import type { TripPhase } from '@/features/records/time/tripPhase';
import { axisX } from '@/features/records/timeline/scale';
import {
  formatRelative,
  formatZoned,
  formatZonedRange,
  isValidTimeZone,
  zonedWallClock,
  zonedWallClockToInstant,
} from '@/utils/zonedTime';

import type { BandCell } from '../accountBandModel';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// ---------------------------------------------------------------------------
// Groups and their queries
// ---------------------------------------------------------------------------

export type TripGroupId = TripPhase;

/** Display order: what is happening, what is next, what was, what has no date. */
export const TRIP_GROUPS: readonly TripGroupId[] = ['active', 'future', 'past', 'undated'];

export const TRIP_GROUP_LABEL: Record<TripGroupId, string> = {
  active: 'Now',
  future: 'Upcoming',
  past: 'Past',
  undated: 'Undated',
};

/** Upcoming soonest first; past most recent first (the `(user, start_ts, id)` index). */
const GROUP_ORDERING: Record<TripGroupId, string> = {
  active: 'start_ts',
  future: 'start_ts',
  past: '-start_ts',
  undated: '-id',
};

/** One page of a group. Past and Upcoming show fewer at first (`GROUP_FIRST`). */
export const TRIP_PAGE_SIZE = 25;

/** Rows a group shows before "Show N more": Now all, Upcoming 10, Past 5 (when opened). */
export const GROUP_FIRST: Record<TripGroupId, number> = {
  active: TRIP_PAGE_SIZE,
  future: 10,
  past: 5,
  undated: 5,
};

/** The columns a trip row shows, by the bookings grid's names — so the panel asks for the
 *  same expansions the grid would for them (`COLUMN_EXPANSIONS`). */
const TRIP_ROW_COLUMNS = { guides: true, start_place: true, route: true, guide_app: true, chat: true };

export const TRIP_EXPANSIONS: BookingListExpansion[] = expansionsForColumns(TRIP_ROW_COLUMNS);

/** The `user` namespace's match-by-PK and the `phase` namespace's only operator (both
 *  declared in `buildBookingNamespaces`; the test holds them there). Constants, never
 *  resolved at runtime: a pin that failed to resolve would list every booking. */
export const USER_PIN_OPERATOR = 'exact';
export const PHASE_OPERATOR = 'iexact';

/** One page of one group's request: the user's bookings in that phase, exact count. */
export function tripsListParams(userId: number, group: TripGroupId, page: number): BookingListParams {
  return {
    filters: [
      { namespace: 'user', operator: USER_PIN_OPERATOR, value: String(userId) },
      { namespace: 'phase', operator: PHASE_OPERATOR, value: group },
    ],
    ordering: GROUP_ORDERING[group],
    page,
    page_size: TRIP_PAGE_SIZE,
    count_mode: 'sync',
    expand: TRIP_EXPANSIONS,
  };
}

/** What the panel knows of a group so far. */
export interface TripGroupData {
  rows: BookingListItem[];
  /** Exact total; null until the first page arrives. */
  count: number | null;
  /** The sync count stopped at its cap (10 000). */
  capped: boolean;
  hasMore: boolean;
}

export type TripGroups = Record<TripGroupId, TripGroupData>;

const countWord = (n: number, capped: boolean) => (capped ? `${n.toLocaleString('en-US')}+` : n.toLocaleString('en-US'));

/** "1 now · 4 upcoming · 27 past" — the groups that have any; "No trips" when none. */
export function countsText(groups: TripGroups): string {
  const words: Record<TripGroupId, string> = { active: 'now', future: 'upcoming', past: 'past', undated: 'undated' };
  const parts = TRIP_GROUPS.filter((g) => (groups[g].count ?? 0) > 0).map(
    (g) => `${countWord(groups[g].count!, groups[g].capped)} ${words[g]}`,
  );
  return parts.length ? parts.join(' · ') : 'No trips';
}

export function totalTrips(groups: TripGroups): number | null {
  if (TRIP_GROUPS.some((g) => groups[g].count === null)) return null;
  return TRIP_GROUPS.reduce((n, g) => n + (groups[g].count ?? 0), 0);
}

// ---------------------------------------------------------------------------
// One trip
// ---------------------------------------------------------------------------

const ms = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

/** Group_Tour_User.STATE_CANCELLED / STATE_CANCELLED_ON_HOLD. */
const CANCELLED_STATES = new Set([2, 3]);
/** Group_Tour_User.STATE_BOOKED. */
export const BOOKED_STATE = 1;

export const isCancelled = (row: Pick<BookingListItem, 'booking_state'>) => CANCELLED_STATES.has(row.booking_state);

export const isStaffRow = (row: Pick<BookingListItem, 'participant_kind'>) =>
  row.participant_kind === 'guide' || row.participant_kind === 'staff';

/** The zone a trip's times are read in: its own, else UTC (labelled by the caller). */
export const tripZone = (row: Pick<BookingListItem, 'timezone'>) => (isValidTimeZone(row.timezone) ? row.timezone : 'UTC');

/** A calendar day in `tz` as a day number (days since the epoch of that wall date). */
function dayNumber(t: number, tz: string): number {
  const w = zonedWallClock(new Date(t), tz);
  return Math.round(Date.UTC(w.year, w.month - 1, w.day) / DAY);
}

/** Calendar days a timed trip touches in its zone; an end at exactly midnight belongs to
 *  the day before. */
export function tripDays(start: number, end: number, tz: string): number {
  return Math.max(1, dayNumber(Math.max(start, end - 1), tz) - dayNumber(start, tz) + 1);
}

/** An all-day trip's length in days, from its duration (its end is the midnight after). */
function allDayDays(row: Pick<BookingListItem, 'duration_s'>): number {
  return Math.max(1, Math.round((row.duration_s ?? DAY / 1000) / (DAY / 1000)));
}

export interface TripWhen {
  /** "12–14 Oct · 2 d 9 h", "Fri 21 Nov · 07:45–13:45", active "30 Sep–6 Oct · day 3 of 7". */
  main: string;
  /** An active multi-day trip: "day 3 of 7" (in place of the length at the end of `main`). */
  dayOf: string | null;
  /** The muted line's parts: how far away, the end of an all-day trip, a stay's end, the
   *  zone when it is not the axis zone. */
  sub: string[];
}

/** When a trip is, in its own zone. */
export function tripWhen(
  row: Pick<BookingListItem, 'start_ts' | 'end_ts' | 'end_ts_type' | 'all_day' | 'duration_s' | 'timezone'>,
  axisZone: string,
  now: Date = new Date(),
): TripWhen {
  const tz = tripZone(row);
  const start = ms(row.start_ts);
  const end = ms(row.end_ts);
  const zoneNote = row.timezone && isValidTimeZone(row.timezone) ? (tz !== axisZone ? tz : null) : 'UTC · tour zone unknown';
  const stay = row.end_ts_type === 1 ? 'ends: per booking' : null;
  if (start === null) return { main: 'No start', dayOf: null, sub: [stay, zoneNote].filter((s): s is string => Boolean(s)) };

  const t = now.getTime();
  const opts = { zone: false, now } as const;
  // `range` — the dates (and times on a single day); `days` — calendar days, null when
  // the trip has no length; `length` — what follows a range of dates: an all-day trip's
  // days, else the real span (a 6-hour overnight trip touches two dates and is "6 h").
  let range: string;
  let days: number | null = null;
  let length: string | null = null;
  let endNote: string | null = null;
  let longTrip = false;
  if (row.all_day) {
    range = `${formatZoned(start, tz, { ...opts, time: false })} · all day`;
    days = allDayDays(row);
    longTrip = true;
    if (days > 1) length = `${days} days`;
    // The booking record's After node: the end as it is stored, the next midnight.
    if (end !== null) endNote = `ends ${formatZoned(end, tz, opts)}`;
  } else if (end === null || end <= start) {
    range = formatZoned(start, tz, opts);
  } else {
    days = tripDays(start, end, tz);
    const spanS = row.duration_s ?? (end - start) / 1000;
    longTrip = spanS >= DAY / 1000;
    range = days === 1 ? formatZonedRange(start, end, tz, opts) : formatZonedRange(start, end, tz, { ...opts, time: false });
    if (days > 1) length = formatTripDuration(spanS);
  }

  const active = start <= t && (end ?? start) > t;
  // "day 3 of 7" counts calendar days, so only a trip of a day or more has them.
  const dayOf =
    active && longTrip && days !== null && days > 1
      ? `day ${Math.min(days, dayNumber(t, tz) - dayNumber(start, tz) + 1)} of ${days}`
      : null;
  const main = dayOf ? `${range} · ${dayOf}` : length ? `${range} · ${length}` : range;

  let rel: string;
  if (start > t) rel = formatRelative(start, { now });
  else if (active) rel = end !== null ? `ends ${formatRelative(end, { now })}` : `started ${formatRelative(start, { now })}`;
  else rel = `ended ${formatRelative(end ?? start, { now })}`;

  return { main, dayOf, sub: [endNote, rel, stay, zoneNote].filter((s): s is string => Boolean(s)) };
}

// ---------------------------------------------------------------------------
// The schedule axis
// ---------------------------------------------------------------------------

export type AxisRangeId = '2w' | '3m' | '1y';

/** The range presets: the window around now, a third before it and two thirds after
 *  (the default `3m` is the plan's [now − 30 d, now + 60 d]). */
export const AXIS_RANGES: Record<AxisRangeId, { label: string; before: number; after: number }> = {
  '2w': { label: '2 wk', before: 5 * DAY, after: 9 * DAY },
  '3m': { label: '3 mo', before: 30 * DAY, after: 60 * DAY },
  '1y': { label: '1 yr', before: 120 * DAY, after: 245 * DAY },
};

export const DEFAULT_AXIS_RANGE: AxisRangeId = '3m';

export interface AxisWindow {
  from: number;
  to: number;
}

export function axisWindow(range: AxisRangeId, now: Date = new Date()): AxisWindow {
  const r = AXIS_RANGES[range];
  return { from: now.getTime() - r.before, to: now.getTime() + r.after };
}

/** The zone most of the loaded trips are in (ties: the first met); UTC when none has one. */
export function dominantZone(rows: Pick<BookingListItem, 'timezone'>[]): string {
  const counts = new Map<string, number>();
  for (const r of rows) {
    if (isValidTimeZone(r.timezone)) counts.set(r.timezone, (counts.get(r.timezone) ?? 0) + 1);
  }
  let best = 'UTC';
  let n = 0;
  for (const [zone, c] of counts) {
    if (c > n) {
      best = zone;
      n = c;
    }
  }
  return best;
}

export interface AxisTick {
  x: number;
  label: string;
}

/** Local midnight of a wall date in `tz` (the date normalised: day 32 is next month). */
function midnight(year: number, month: number, day: number, tz: string): number {
  const d = new Date(Date.UTC(year, month - 1, day));
  return zonedWallClockToInstant(
    { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(), hour: 0, minute: 0 },
    tz,
  ).getTime();
}

/** A tick label's rough width: the caption face (12 px Inter, tabular digits) per
 *  character, plus the gap two labels keep between them. */
export const TICK_CHAR_PX = 6.5;
export const TICK_GAP_PX = 12;

export const tickLabelPx = (label: string) => label.length * TICK_CHAR_PX;

/** Date ticks across the window in `tz`: daily up to three weeks ("5 Oct", every other
 *  day past ten days), Mondays up to four months, else month starts ("Oct", "Jan 2027").
 *  Given the plot's width, only every n-th tick is kept, so no two labels touch (a phone
 *  draws fewer ticks than a laptop). */
export function axisTicks(win: AxisWindow, tz: string, widthPx = 0): AxisTick[] {
  const ticks = calendarTicks(win, tz);
  if (widthPx <= 0 || ticks.length < 2) return ticks;
  const widest = Math.max(...ticks.map((t) => tickLabelPx(t.label))) + TICK_GAP_PX;
  let closest = Infinity;
  for (let i = 1; i < ticks.length; i++) closest = Math.min(closest, (ticks[i]!.x - ticks[i - 1]!.x) * widthPx);
  const every = Math.max(1, Math.ceil(widest / closest));
  return ticks.filter((_, i) => i % every === 0);
}

function calendarTicks(win: AxisWindow, tz: string): AxisTick[] {
  const span = win.to - win.from;
  const first = zonedWallClock(new Date(win.from), tz);
  const x = (t: number) => axisX(t, null, [win.from, win.to]);
  const ticks: AxisTick[] = [];
  const dayLabel = (t: number) => formatZoned(t, tz, { weekday: false, time: false, zone: false, year: 'never' });

  if (span <= 21 * DAY) {
    const every = span > 10 * DAY ? 2 : 1;
    for (let i = 1, t = midnight(first.year, first.month, first.day + 1, tz); t < win.to; i++) {
      if ((i - 1) % every === 0) ticks.push({ x: x(t), label: dayLabel(t) });
      t = midnight(first.year, first.month, first.day + 1 + i, tz);
    }
    return ticks;
  }
  if (span <= 125 * DAY) {
    // The first Monday after the window opens (wall-date weekday: Date.UTC is exact).
    const weekday = new Date(Date.UTC(first.year, first.month - 1, first.day)).getUTCDay();
    const toMonday = ((8 - weekday) % 7) || 7;
    for (let i = 0; ; i++) {
      const t = midnight(first.year, first.month, first.day + toMonday + 7 * i, tz);
      if (t >= win.to) break;
      ticks.push({ x: x(t), label: dayLabel(t) });
    }
    return ticks;
  }
  for (let i = 1; ; i++) {
    const d = new Date(Date.UTC(first.year, first.month - 1 + i, 1));
    const t = midnight(d.getUTCFullYear(), d.getUTCMonth() + 1, 1, tz);
    if (t >= win.to) break;
    const month = formatZoned(t, tz, { weekday: false, time: false, zone: false, year: 'never' }).split(' ')[1] ?? '';
    ticks.push({ x: x(t), label: d.getUTCMonth() === 0 ? `${month} ${d.getUTCFullYear()}` : month });
  }
  return ticks;
}

/** A placed trip: its bar (or the booked tick) and, for a staff booking, its windows. */
export interface PlacedTrip {
  row: BookingListItem;
  group: TripGroupId;
  lane: number;
  x0: number;
  x1: number;
  /** Zero length: the 3×14 px booked tick at `x0`. */
  tick: boolean;
  /** The guide-app window; `dashed` when the guide's cache does not hold the booking. */
  app: { x0: number; x1: number; dashed: boolean } | null;
  /** The chat window, start to predicted leave. */
  chat: { x0: number; x1: number } | null;
}

export interface Overflow {
  x: number;
  count: number;
}

export interface AxisLayout {
  trips: PlacedTrip[];
  lanes: number;
  overflow: Overflow[];
  /** Some placed trip is a staff booking (the legend names its lines). */
  staffLines: boolean;
}

export const MAX_LANES = 5;

/** The span of a window on the axis, clipped; null when it misses the window. */
function span(from: number | null, to: number | null, win: AxisWindow): { x0: number; x1: number } | null {
  if (from === null || to === null || to < from || to < win.from || from > win.to) return null;
  const x = (t: number) => axisX(t, null, [win.from, win.to]);
  return { x0: x(from), x1: x(to) };
}

/** The staff windows under a staff booking's bar. */
function staffWindows(row: BookingListItem, start: number, end: number, win: AxisWindow): Pick<PlacedTrip, 'app' | 'chat'> {
  if (!isStaffRow(row)) return { app: null, chat: null };
  const v = row.staff_visibility;
  const appSpan = v?.app_window ? span(ms(v.app_window.from) ?? start, ms(v.app_window.to) ?? end, win) : null;
  const c = row.chat;
  const windowed = c && (c.state === 'in_chat' || c.state === 'joins' || c.state === 'left');
  const chatSpan = windowed
    ? span(
        ms(c.joins_at) ?? ms(c.channel?.starts_at) ?? start,
        ms(c.leaves_at) ?? ms(c.channel?.ends_at) ?? end,
        win,
      )
    : null;
  return {
    app: appSpan && v ? { ...appSpan, dashed: v.cache.state !== 'published' } : null,
    chat: chatSpan,
  };
}

/** Places every dated trip that touches the window in at most `maxLanes` packed lanes,
 *  soonest first; what does not fit is counted in "+N overlapping" markers, one per run
 *  of overlapping trips. */
export function axisLayout(
  groups: { group: TripGroupId; rows: BookingListItem[] }[],
  win: AxisWindow,
  maxLanes = MAX_LANES,
): AxisLayout {
  const gap = (win.to - win.from) * 0.004;
  const items = groups
    .flatMap(({ group, rows }) => rows.map((row) => ({ row, group })))
    .map((it) => {
      const s = ms(it.row.start_ts);
      return { ...it, s, e: s === null ? null : Math.max(s, ms(it.row.end_ts) ?? s) };
    })
    .filter((it): it is typeof it & { s: number; e: number } => it.s !== null && it.e! >= win.from && it.s <= win.to)
    .sort((a, b) => a.s - b.s || a.row.id - b.row.id);

  const laneEnds: number[] = [];
  const placed: PlacedTrip[] = [];
  const spilled: { s: number; e: number }[] = [];
  for (const it of items) {
    const extent = Math.max(it.e, it.s + gap);
    let lane = laneEnds.findIndex((end) => end + gap <= it.s);
    if (lane === -1 && laneEnds.length < maxLanes) lane = laneEnds.length;
    if (lane === -1) {
      spilled.push({ s: it.s, e: extent });
      continue;
    }
    laneEnds[lane] = extent;
    const x = (t: number) => axisX(t, null, [win.from, win.to]);
    placed.push({
      row: it.row,
      group: it.group,
      lane,
      x0: x(it.s),
      x1: x(it.e),
      tick: it.e === it.s,
      ...staffWindows(it.row, it.s, it.e, win),
    });
  }

  const overflow: Overflow[] = [];
  let run: { s: number; e: number; count: number } | null = null;
  for (const sp of spilled) {
    if (run && sp.s <= run.e) {
      run.e = Math.max(run.e, sp.e);
      run.count += 1;
    } else {
      if (run) overflow.push({ x: axisX(run.s, null, [win.from, win.to]), count: run.count });
      run = { ...sp, count: 1 };
    }
  }
  if (run) overflow.push({ x: axisX(run.s, null, [win.from, win.to]), count: run.count });

  return {
    trips: placed,
    lanes: laneEnds.length,
    overflow,
    staffLines: placed.some((p) => p.app || p.chat),
  };
}

/** What the loaded pages leave out of the window: before the oldest loaded past trip while
 *  more past pages exist (Load earlier), after the latest loaded upcoming one while more
 *  upcoming pages exist (Load later) — the parts the axis hatches. */
export function unloadedSpans(
  past: Pick<TripGroupData, 'rows' | 'hasMore'>,
  future: Pick<TripGroupData, 'rows' | 'hasMore'>,
  win: AxisWindow,
): { earlier: number | null; later: number | null } {
  const x = (t: number) => axisX(t, null, [win.from, win.to]);
  const oldest = past.rows.length ? ms(past.rows[past.rows.length - 1]!.start_ts) : null;
  const latest = future.rows.length ? ms(future.rows[future.rows.length - 1]!.start_ts) : null;
  return {
    earlier: past.hasMore && oldest !== null && oldest > win.from ? x(oldest) : null,
    later: future.hasMore && latest !== null && latest < win.to ? x(latest) : null,
  };
}

// ---------------------------------------------------------------------------
// The account band's Trips cell
// ---------------------------------------------------------------------------

/** "On Rome Highlights · day 3 of 7" / "Next: 12 Oct · Amalfi Coast" / "Last: 3 Sep · …",
 *  the counts under it. Null for a system user with no trips (the cell is hidden); "No
 *  trips" for anyone else. */
export function tripsBandCell(
  groups: TripGroups,
  kind: 'system' | 'staff' | 'guest',
  now: Date = new Date(),
): BandCell | null {
  const total = totalTrips(groups);
  const cell = { id: 'trips' as const, title: 'Trips' };
  if (total === null) return { ...cell, datum: 'Loading…', detail: null };
  if (total === 0) return kind === 'system' ? null : { ...cell, datum: 'No trips', detail: null };
  const day = (iso: string, row: BookingListItem) =>
    formatZoned(iso, tripZone(row), { weekday: false, time: false, zone: false, now });
  const active = groups.active.rows[0];
  const next = groups.future.rows[0];
  const last = groups.past.rows[0];
  let datum: string;
  if (active) {
    const { dayOf } = tripWhen(active, tripZone(active), now);
    datum = `On ${active.tour.name}${dayOf ? ` · ${dayOf}` : ''}`;
  } else if (next?.start_ts) datum = `Next: ${day(next.start_ts, next)} · ${next.tour.name}`;
  else if (last?.start_ts) datum = `Last: ${day(last.end_ts ?? last.start_ts, last)} · ${last.tour.name}`;
  else datum = countsText(groups);
  return { ...cell, datum, detail: countsText(groups) };
}

/** The empty line of a group: what is not there, and the nearest thing that is. */
export function emptyGroupText(group: TripGroupId, groups: TripGroups, now: Date = new Date()): string {
  const next = groups.future.rows[0];
  const last = groups.past.rows[0];
  const day = (iso: string, row: BookingListItem) =>
    formatZoned(iso, tripZone(row), { weekday: false, time: false, zone: false, now });
  switch (group) {
    case 'active':
      return next?.start_ts ? `Not on a trip now · next starts ${formatRelative(next.start_ts, { now })}` : 'Not on a trip now';
    case 'future':
      return last?.start_ts ? `No upcoming trips · last trip ended ${day(last.end_ts ?? last.start_ts, last)}` : 'No upcoming trips';
    case 'past':
      return 'No past trips';
    default:
      return 'No undated trips';
  }
}

/** The muted line of the Trip column: tenant · departure · order · booking system. */
export function tripMeta(row: BookingListItem): string {
  return [
    row.tenant?.code || row.tenant?.name,
    row.departure?.name || row.departure_key || null,
    row.order_ref ? `Order ${row.order_ref}` : null,
    row.integration ? integrationName(row.integration) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** The With column's main line: a guest booking names its guides; a staff booking how many
 *  guests and with whom (the bookings grid's Guides cell, `guidesText`). */
export function withText(row: BookingListItem): string {
  const text = guidesText(row);
  if (isStaffRow(row) || row.participant_kind === 'observer') return text;
  return text ? `Guides: ${text}` : '';
}
